import { Close } from "@mui/icons-material";
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Link,
	Paper,
	Stack,
	Typography,
} from "@mui/material";

import AmundiImage from "AdvisoryManagement/Assets/Images/amundi.png";
import CPPImage from "AdvisoryManagement/Assets/Images/CPP.png";
import CalculatorImage from "AdvisoryManagement/Assets/Images/calculator.png";
import GeneraliCeskaImage from "AdvisoryManagement/Assets/Images/generali_ceska.png";
import GeneraliPenzijniImage from "AdvisoryManagement/Assets/Images/generali_penzijni.png";
import InvestikaImage from "AdvisoryManagement/Assets/Images/investika.png";
import KooperativaImage from "AdvisoryManagement/Assets/Images/Kooperativa.png";
import UniquaImage from "AdvisoryManagement/Assets/Images/UNIQA_CZ.jpg";
import UplusImage from "AdvisoryManagement/Assets/Images/uplus.png";

type ExtranetsDialogProps = {
	open: boolean;
	onClose: () => void;
	categoryCode?: string | null;
};

type ExtranetItem = {
	name: string;
	image: string;
	url: string;
	category: "insurance" | "investment" | "bank" | "financial" | "other";
};

const extranetItems: ExtranetItem[] = [
	// Pojišťovny
	{
		name: "ČPP SUS Plus",
		image: CPPImage,
		url: "#",
		category: "insurance",
	},
	{
		name: "Kooperativa",
		image: KooperativaImage,
		url: "#",
		category: "insurance",
	},
	{
		name: "UNIQA",
		image: UniquaImage,
		url: "#",
		category: "insurance",
	},
	{
		name: "Youplus",
		image: UplusImage,
		url: "#",
		category: "insurance",
	},
	{
		name: "Generali Česká pojišťovna - LISA",
		image: GeneraliCeskaImage,
		url: "#",
		category: "insurance",
	},
	{
		name: "Generali Česká pojišťovna - HUGO",
		image: GeneraliCeskaImage,
		url: "#",
		category: "insurance",
	},
	// Investiční společnosti
	{
		name: "Amundi - Fundoo",
		image: AmundiImage,
		url: "#",
		category: "investment",
	},
	{
		name: "Amundi - NEMO",
		image: AmundiImage,
		url: "#",
		category: "investment",
	},
	{
		name: "Investika",
		image: InvestikaImage,
		url: "#",
		category: "investment",
	},
	// Penzijní společnosti
	{
		name: "Generali Penzijní společnost - PEPA",
		image: GeneraliPenzijniImage,
		url: "#",
		category: "financial",
	},
	// Ostatní
	{
		name: "Extranety bez jednotného přihlášení",
		image: CalculatorImage,
		url: "#",
		category: "other",
	},
];

type CategorySection = {
	title: string;
	categoryTypes: Array<
		"insurance" | "investment" | "bank" | "financial" | "other"
	>;
	priority: number;
};

const categorySections: CategorySection[] = [
	{
		title: "Pojišťovny",
		categoryTypes: ["insurance"],
		priority: 0,
	},
	{
		title: "Investiční společnosti",
		categoryTypes: ["investment"],
		priority: 1,
	},
	{
		title: "Banky",
		categoryTypes: ["bank"],
		priority: 2,
	},
	{
		title: "Penzijní společnosti",
		categoryTypes: ["financial"],
		priority: 3,
	},
	{
		title: "Ostatní",
		categoryTypes: ["other"],
		priority: 4,
	},
];

const getCategoryPriority = (categoryCode?: string | null): number => {
	if (!categoryCode) return 0;

	if (
		categoryCode.includes("LIFE") ||
		categoryCode.includes("NONLIFE") ||
		categoryCode.includes("INSURANCE")
	) {
		return 0;
	}

	if (
		categoryCode.includes("INVEST") ||
		categoryCode.includes("FUND") ||
		categoryCode.includes("SECURITIES")
	) {
		return 1;
	}

	if (categoryCode.includes("PENSION") || categoryCode.includes("RETIREMENT")) {
		return 3;
	}

	return 0;
};

export const ExtranetsDialog = ({
	open,
	onClose,
	categoryCode,
}: ExtranetsDialogProps) => {
	const prioritySection = getCategoryPriority(categoryCode);

	const sortedSections = [...categorySections].sort((a, b) => {
		if (a.priority === prioritySection) return -1;
		if (b.priority === prioritySection) return 1;
		return a.priority - b.priority;
	});

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					paddingBottom={2}
				>
					<Typography variant="h2" fontSize={24} fontWeight={600}>
						Extranety
					</Typography>
					<IconButton onClick={onClose} size="small">
						<Close />
					</IconButton>
				</Stack>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={3}>
					{sortedSections.map((section) => {
						const items = extranetItems.filter((item) =>
							section.categoryTypes.includes(item.category),
						);

						if (items.length === 0) return null;

						return (
							<Box key={section.title}>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									sx={{ mb: 2, color: "text.primary" }}
								>
									{section.title}
								</Typography>
								<Divider sx={{ mb: 2 }} />
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											xs: "1fr",
											sm: "repeat(2, 1fr)",
											md: "repeat(3, 1fr)",
										},
										gap: 2,
									}}
								>
									{items.map((item) => (
										<Link
											key={item.name}
											href={item.url}
											target="_blank"
											rel="noopener noreferrer"
											underline="none"
										>
											<Paper
												elevation={0}
												sx={{
													p: 2,
													border: "1px solid #E9EAEB",
													borderRadius: 1,
													transition: "all 0.2s",
													"&:hover": {
														backgroundColor: "#FAFAFA",
														borderColor: "primary.main",
														transform: "translateY(-2px)",
														boxShadow: 1,
													},
													cursor: "pointer",
												}}
											>
												<Stack spacing={1.5} alignItems="center">
													<Box
														component="img"
														src={item.image}
														alt={item.name}
														sx={{
															width: "100%",
															height: 80,
															objectFit: "contain",
														}}
													/>
													<Typography
														variant="body2"
														textAlign="center"
														fontWeight={500}
														sx={{ color: "text.primary" }}
													>
														{item.name}
													</Typography>
												</Stack>
											</Paper>
										</Link>
									))}
								</Box>
							</Box>
						);
					})}
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
